import {
  AlertTriangle,
  BarChart3,
  Database,
  FileSearch,
  Home,
  LayoutDashboard,
  LayoutGrid,
  Sparkles,
  Plus,
  type LucideIcon,
} from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import { api, type DatasetSummary, type WidgetAsset } from '@/lib/api';

/**
 * Globale Steuerung der Command-Palette.
 *
 * Komponenten (z.B. AppHeader) koennen via `useCommandPalette()` an die
 * Palette herankommen und sie oeffnen/schliessen. Der Provider haengt
 * an der Wurzel der App.
 */
interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error('useCommandPalette must be used within a CommandPaletteProvider');
  }
  return ctx;
}

/**
 * Statische Quick-Actions (immer sichtbar, navigieren nur).
 */
interface QuickAction {
  id: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  to: string;
  /** Zusaetzliche Keywords fuer die cmdk-Filterung (z.B. "neu", "create"). */
  keywords?: string[];
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'action:new-source',
    label: 'Quelle hinzufügen',
    hint: 'Abruf, fremder Katalog, Bebauungsplan oder Gerät',
    icon: Plus,
    to: '/sources/new',
    keywords: ['neu', 'new', 'create', 'dataset', 'quelle', 'source', 'anlegen', 'wizard'],
  },
  {
    id: 'action:builder',
    label: 'Per Prompt erstellen',
    hint: 'KI-gestützt Widget/Sicht/Prognose/Alert bauen',
    icon: Sparkles,
    to: '/builder',
    keywords: ['prompt', 'ai', 'llm', 'generieren', 'builder', 'kI'],
  },
  {
    id: 'action:gallery',
    label: 'Zu den Auswertungen',
    hint: 'Widget-Übersicht',
    icon: LayoutGrid,
    to: '/gallery',
    keywords: ['galerie', 'widgets', 'overview'],
  },
  {
    id: 'action:dashboard',
    label: 'Zum Dashboard',
    hint: 'Mein Dashboard',
    icon: LayoutDashboard,
    to: '/dashboard',
    keywords: ['dashboard', 'mein dashboard'],
  },
  {
    id: 'action:uebersicht',
    label: 'Zur Übersicht',
    hint: 'Startseite nach der Anmeldung',
    icon: Home,
    to: '/uebersicht',
    keywords: ['übersicht', 'start', 'home'],
  },
  {
    id: 'action:alerts',
    label: 'Meldungen',
    hint: 'Alert-Übersicht',
    icon: AlertTriangle,
    to: '/alerts',
    keywords: ['alerts', 'warnungen', 'events'],
  },
  {
    id: 'action:audit',
    label: 'Protokoll (Audit-Log)',
    hint: 'Audit-Übersicht',
    icon: FileSearch,
    to: '/audit',
    keywords: ['audit', 'log', 'historie', 'history'],
  },
];

interface SearchData {
  datasets: DatasetSummary[];
  widgets: WidgetAsset[];
}

interface SearchState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  data: SearchData;
  error?: string;
}

const EMPTY_SEARCH_DATA: SearchData = { datasets: [], widgets: [] };

/**
 * Provider — wraps die App und liefert die Steuerung an Konsumenten.
 *
 * Rendert die Palette selbst NICHT, nur den State. `<CommandPalette />` ist
 * eine separate Komponente, damit der Render-Aufruf am Root liegt und die
 * Palette ueber dem ganzen App-Frame liegt.
 */
export function CommandPaletteProvider({ children }: { children: ReactNode }): JSX.Element {
  const [open, setOpenState] = useState(false);

  const setOpen = useCallback((value: boolean) => {
    setOpenState(value);
  }, []);

  const toggle = useCallback(() => {
    setOpenState((v) => !v);
  }, []);

  // Globaler Cmd+K / Ctrl+K Listener.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      // Nur Cmd/Ctrl + K, sonst keine andere Modifier-Kombination.
      const key = event.key.toLowerCase();
      if (key === 'k' && (event.metaKey || event.ctrlKey)) {
        // Verhindert Browser-Default-Search-Bar-Fokus in einigen Browsern.
        event.preventDefault();
        setOpenState((v) => !v);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const value = useMemo<CommandPaletteContextValue>(
    () => ({ open, setOpen, toggle }),
    [open, setOpen, toggle],
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandPalette />
    </CommandPaletteContext.Provider>
  );
}

/**
 * Die Palette selbst. Sie laedt Datasets/Widgets lazy beim ersten Open
 * und cached die Daten im selben React-Tree.
 */
export function CommandPalette(): JSX.Element {
  const { open, setOpen } = useCommandPalette();
  const navigate = useNavigate();

  const [search, setSearch] = useState<SearchState>({ status: 'idle', data: EMPTY_SEARCH_DATA });
  const triggeredLoad = useRef(false);

  // Lazy-Load: Datasets + Widgets beim ersten Oeffnen.
  useEffect(() => {
    if (!open) return;
    if (triggeredLoad.current) return;
    triggeredLoad.current = true;

    setSearch({ status: 'loading', data: EMPTY_SEARCH_DATA });

    void (async () => {
      try {
        const [datasetsRes, widgetsRes] = await Promise.all([
          api.listDatasets({ limit: 50 }),
          api.listWidgets(),
        ]);
        setSearch({
          status: 'ready',
          data: {
            datasets: datasetsRes.items,
            widgets: widgetsRes.items,
          },
        });
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        setSearch({
          status: 'error',
          data: EMPTY_SEARCH_DATA,
          error: message,
        });
      }
    })();
  }, [open]);

  const handleSelect = useCallback(
    (to: string) => {
      setOpen(false);
      navigate(to);
    },
    [navigate, setOpen],
  );

  return (
    <CommandDialog open={open} onOpenChange={setOpen} label="Befehle und Suche">
      <CommandInput placeholder="Suchen oder Aktion ausführen..." />
      <CommandList>
        <CommandEmpty>Keine Treffer.</CommandEmpty>

        <CommandGroup heading="Aktionen">
          {QUICK_ACTIONS.map((action) => {
            const Icon = action.icon;
            return (
              <CommandItem
                key={action.id}
                value={`${action.label} ${action.hint ?? ''} ${(action.keywords ?? []).join(' ')}`}
                onSelect={() => handleSelect(action.to)}
              >
                <Icon className="text-muted-foreground" />
                <span className="flex-1">{action.label}</span>
                {action.hint !== undefined && (
                  <span className="text-xs text-muted-foreground">{action.hint}</span>
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>

        {search.status === 'loading' && (
          <CommandLoading>Lade Datensätze und Widgets...</CommandLoading>
        )}

        {search.status === 'error' && (
          <>
            <CommandSeparator />
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              Konnte Datensätze/Widgets nicht laden — bitte neu anmelden.
            </div>
          </>
        )}

        {search.status === 'ready' && search.data.datasets.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Datensätze">
              {search.data.datasets.map((ds) => (
                <CommandItem
                  key={`ds:${ds.id}`}
                  value={`${ds.title} ${ds.slug} ${ds.silo} ${(ds.tags ?? []).join(' ')}`}
                  onSelect={() => handleSelect(`/catalog/${ds.id}`)}
                >
                  <Database className="text-muted-foreground" />
                  <div className="flex flex-1 flex-col overflow-hidden">
                    <span className="truncate">{ds.title || ds.slug}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {ds.slug} · {ds.silo}
                    </span>
                  </div>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {search.status === 'ready' && search.data.widgets.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Widgets">
              {search.data.widgets.map((w) => (
                <CommandItem
                  key={`w:${w.id}`}
                  value={`${w.title} ${w.widget_type}`}
                  onSelect={() => handleSelect('/gallery')}
                >
                  <BarChart3 className="text-muted-foreground" />
                  <div className="flex flex-1 flex-col overflow-hidden">
                    <span className="truncate">{w.title}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      {w.widget_type}
                    </span>
                  </div>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />
        <div className="px-3 py-2 text-[11px] text-muted-foreground">
          Tipp: ⌘K / Ctrl+K zum Öffnen, Esc zum Schließen, ↑↓ zum Navigieren, ↵ zum
          Auswählen.
        </div>
      </CommandList>
    </CommandDialog>
  );
}
